import { useEffect, useRef, useState } from 'react';

const SLIDES = [
  { type: 'image', src: '/release/sukalpa-release-2.jpeg' },
  { type: 'video', src: '/release/video/video2.mp4' },
  { type: 'image', src: '/release/sukalpa-release-1.jpeg' },
  { type: 'image', src: '/release/sukalpa-release-3.jpeg' },
  { type: 'image', src: '/release/sukalpa-release-4.jpeg' },
  { type: 'video', src: '/release/video/video1.mp4' },
];

export default function SplashScreen({ onClose }) {
  const [current, setCurrent] = useState(0);
  const [needsUnmute, setNeedsUnmute] = useState(false);
  const videoRefs = useRef([]);

  const goToNext = () => setCurrent((prev) => (prev + 1) % SLIDES.length);

  // images auto-advance on a timer, videos advance when they finish playing
  useEffect(() => {
    if (SLIDES[current].type === 'video') return undefined;
    const timer = setInterval(goToNext, 3000);
    return () => clearInterval(timer);
  }, [current]);

  useEffect(() => {
    // stop every other video so switching slides never overlaps audio
    videoRefs.current.forEach((video, index) => {
      if (!video || index === current) return;
      video.pause();
      video.currentTime = 0;
    });

    if (SLIDES[current].type !== 'video') return;
    const video = videoRefs.current[current];
    if (!video) return;
    video.currentTime = 0;
    setNeedsUnmute(false);
    // browsers block unmuted autoplay without a prior user gesture, so fall back to muted playback
    video.play().catch(() => {
      video.muted = true;
      setNeedsUnmute(true);
      video.play().catch(() => {});
    });
  }, [current]);

  const handleUnmute = () => {
    const video = videoRefs.current[current];
    if (!video) return;
    video.muted = false;
    video.play().catch(() => {});
    setNeedsUnmute(false);
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    // dimmed backdrop lets the website remain visible behind the modal
    <div
      className="fixed inset-0 z-[10000] bg-black/70 flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl aspect-[4/5] sm:aspect-[16/10] bg-black rounded-2xl shadow-2xl overflow-hidden animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Slides */}
        {SLIDES.map((slide, index) =>
          slide.type === 'video' ? (
            <video
              key={slide.src}
              ref={(el) => (videoRefs.current[index] = el)}
              src={slide.src}
              playsInline
              onEnded={goToNext}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
                index === current ? 'opacity-100' : 'opacity-0'
              }`}
            />
          ) : (
            <img
              key={slide.src}
              src={slide.src}
              alt={`Sukalpa slide ${index + 1}`}
              className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-1000 ease-in-out ${
                index === current ? 'opacity-100' : 'opacity-0'
              }`}
            />
          )
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close splash screen"
          className="absolute top-3 right-3 z-20 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-white/90 hover:bg-white text-gray-800 text-xl font-bold shadow-lg transition"
        >
          &times;
        </button>

        {/* Shown when the browser blocked autoplay with sound */}
        {needsUnmute && SLIDES[current].type === 'video' && (
          <button
            onClick={handleUnmute}
            className="absolute top-3 left-3 z-20 px-3 py-2 rounded-full bg-white/90 hover:bg-white text-gray-800 text-sm font-semibold shadow-lg transition"
          >
            🔊 Tap for sound
          </button>
        )}

        {/* Slide indicators */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {SLIDES.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrent(index)}
              aria-label={`Go to slide ${index + 1}`}
              className={`w-2.5 h-2.5 rounded-full transition ${
                index === current ? 'bg-white' : 'bg-white/40'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
